import { ReactNode } from "react";
import { IconLike } from "components/Icon/Icon";
import { imageProps } from "components/ImageIcon/ImageIcon";
import { ProvisioningStateDot } from "components/ProvisioningStateDot/ProvisioningStateDot";
import dotStyle from "components/ProvisioningStateDot/ProvisioningStateDot.module.scss";
import RecordLabel from "components/RecordLabel";
import StatusBadge from "components/StatusBadge/StatusBadge";
import Tooltip from "components/Tooltip/Tooltip";
import { FeConfigType, FeRecordType } from "config/config";
import { getCustomObjectProvisioningStateColors } from "config/ts-utils";
import { CustomObjectProvisioningState } from "graphql/generated/graphql";
import { getRelativeUrl, getTypedLowerCase, prefersIdInUrl } from "utils/service";
import { capitalizeFirstLetter } from "utils/utils";

import style from "./DashboardRecordLink.module.scss";

export interface DashboardRecordLinkProps {
    id: string;
    name: string;
    recordType: FeConfigType | string;
    provisioningState?: CustomObjectProvisioningState | null;
    icon?: IconLike;
    /**
     * The record's own stored `icon` value (`mdi:<key>` curated glyph or `data:`
     * upload). Used for record types (e.g. dashboards) whose icon is carried on
     * the row itself rather than fetched from a redux slice via `imageProps`. A
     * store-backed `imageProps` image (project/bundle/custom_object) still wins.
     */
    image?: string | null;
    /** Override link label (e.g. execution status message). */
    displayName?: string;
    /** Total comment count; when > 0 an inert comment-count indicator is shown after the name. */
    commentCount?: number | null;
    /**
     * For a custom object: its object_template id, so the row icon resolves to the
     * template's icon from the complete `objectTemplateSchema` store (the
     * provisioning-state cache only holds non-READY custom objects).
     */
    objectTemplateId?: string | null;
    /** Always-on hover tooltip for the row (e.g. an execution's status message). */
    tooltip?: ReactNode;
}

const ProvisioningStatePill = ({ state }: { state: CustomObjectProvisioningState }) => {
    const stateText = capitalizeFirstLetter(state.toLowerCase().replace(/_/g, " "));

    // A READY custom object shows no dot (see ProvisioningStateDot).
    if (state === "READY") {
        return null;
    }

    return (
        <div className={dotStyle.provisioningStateDotWrapper}>
            <Tooltip overlay={stateText} overlayStyle={{ zIndex: 20000 }}>
                <StatusBadge
                    round
                    type={getCustomObjectProvisioningStateColors(state)}
                    size="small"
                />
            </Tooltip>
        </div>
    );
};

/**
 * Standard dashboard record row: optional provisioning-state pill, type icon, linked name.
 * Matches quick-access / project-tree record rendering.
 */
export const DashboardRecordLink = ({
    id,
    name,
    recordType,
    provisioningState,
    icon,
    image,
    displayName,
    commentCount,
    objectTemplateId,
    tooltip,
}: DashboardRecordLinkProps) => {
    const apiType = getTypedLowerCase(recordType) as FeRecordType;
    const link = prefersIdInUrl(apiType)
        ? getRelativeUrl(apiType as FeConfigType, id, "id")
        : getRelativeUrl(apiType as FeConfigType, name, "name");
    const isCustomObject = apiType === "custom_object";
    const showPillFromData = isCustomObject && provisioningState;
    const showPillFromStore = isCustomObject && !provisioningState;

    const label = (
        <RecordLabel
            name={displayName ?? name}
            link={link}
            iconByType={apiType as FeRecordType}
            icon={icon}
            image={image}
            commentCount={commentCount}
            disableOverflowTooltip={!!tooltip}
            {...imageProps(apiType as FeRecordType, id, objectTemplateId)}
        />
    );

    return (
        <div className={isCustomObject ? dotStyle.relativePositionedContainer : undefined}>
            {showPillFromData ? (
                <ProvisioningStatePill state={provisioningState} />
            ) : showPillFromStore ? (
                <ProvisioningStateDot id={id} />
            ) : null}
            {tooltip ? (
                <Tooltip overlay={tooltip} overlayStyle={{ zIndex: 20000 }} placement="bottomLeft">
                    <div className={style.recordLinkTooltipTrigger}>{label}</div>
                </Tooltip>
            ) : (
                label
            )}
        </div>
    );
};
